import { useRedirectPaymentIntent } from '@hooks/useRedirectPaymentIntent';
import { loadNovapayWidget } from '@utils/novapay';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

const redirectBg = 'hsl(240 0% 2%)';
const redirectGradient =
  'radial-gradient(at 54% 100%, hsla(178, 81%, 60%, 0.24) 0, transparent 50%), radial-gradient(at 68% 100%, hsla(215, 100%, 71%, 0.37) 0, transparent 50%), radial-gradient(at 40% 100%, hsla(167, 100%, 60%, 0.29) 0, transparent 50%)';

const styles: Record<string, CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: redirectBg,
    backgroundImage: redirectGradient
  },
  column: {
    marginTop: 80,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    height: 20
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgb(255 255 255 / 0.2)',
    borderTopColor: 'var(--novapay-on-accent)',
    borderRadius: '50%',
    animation: 'global-loading-spin 0.8s linear infinite'
  },
  loadingText: {
    margin: 0,
    fontSize: 16,
    fontWeight: 400
  },
  errorBox: {
    marginTop: 60,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    paddingLeft: 16,
    paddingRight: 16
  },
  errorTitle: {
    margin: 0,
    fontSize: 36,
    fontWeight: 600
  },
  errorMessage: {
    margin: 0,
    fontSize: 20,
    fontWeight: 400
  }
};

export default function RedirectPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('redirect_token');

  const intentQuery = useRedirectPaymentIntent(token);

  const [isWidgetRendered, setIsWidgetRendered] = useState(false);
  const widgetBootstrapped = useRef(false);

  useEffect(() => {
    if (!intentQuery.isSuccess || !intentQuery.data?.data?.widget_data) {
      return;
    }
    const data = intentQuery.data.data;
    const widgetData = data.widget_data;
    if (!widgetData) {
      return;
    }
    if (widgetBootstrapped.current) {
      return;
    }
    widgetBootstrapped.current = true;

    loadNovapayWidget(data, document);
  }, [intentQuery.isSuccess, intentQuery.data]);

  useEffect(() => {
    if (!intentQuery.isSuccess) {
      return;
    }
    const checkForWidgetInDOM = () => {
      const widgetNode = document.getElementById('headlessui-portal-root');
      if (!widgetNode) {
        setTimeout(checkForWidgetInDOM, 1000);
        return;
      }
      setIsWidgetRendered(true);
    };
    checkForWidgetInDOM();
  }, [intentQuery.isSuccess]);

  if (!token) {
    return <Navigate to="/404" replace />;
  }

  if (intentQuery.isPending) {
    return (
      <div style={styles.root}>
        <div style={styles.column}>
          <div aria-label={t('redirect.loading_your_data')} role="status" style={styles.spinner} />
          <p style={styles.loadingText}>{t('redirect.loading_your_data')}</p>
        </div>
      </div>
    );
  }

  const { displayError } = intentQuery;

  return (
    <div style={styles.root}>
      {intentQuery.isSuccess && !displayError?.status && !isWidgetRendered && (
        <div style={styles.column}>
          <div aria-label={t('redirect.loading_your_data')} role="status" style={styles.spinner} />
          <p style={styles.loadingText}>{t('redirect.loading_your_data')}</p>
        </div>
      )}

      {intentQuery.isError && displayError ? (
        <div style={styles.errorBox}>
          {displayError.status ? <h2 style={styles.errorTitle}>{displayError.status}</h2> : null}
          <p style={styles.errorMessage}>{displayError.message}</p>
        </div>
      ) : null}
    </div>
  );
}
